/**
 * HLOS-168-B: Vault backend "opfs" — implementação atual extraída de
 * `store.ts`. Mantém a lógica OPFS existente (sem mudança de comportamento)
 * para preservar AC HLOS-28 em web/Android.
 *
 * Storage layout (HLOS-28):
 *   navigator.storage.getDirectory() →
 *     <user_id>/vault.json       (vault cifrado)
 *     <user_id>/meta.json        (metadados públicos)
 *     <user_id>/consent.enc      (consent log cifrado)
 *
 * O backend é STATELESS — sem cache interna. Cada operação é uma chamada
 * direta ao OPFS API. VaultStore pode cachear resultado se precisar.
 *
 * Constitucional:
 * - CL-07 (vault local-first): OPFS é local no app sandbox do browser
 *   e no WebView Android.
 * - CL-08 (zero key escrow): bytes são SEMPRE cifrados antes de chegar
 *   aqui (vault-crypto skill).
 * - Não toca LLM, LATENT_DIM, P=1000, etc.
 */

import type { VaultStorage } from "../store";

/**
 * Implementação OPFS do VaultStorage. Extraída de `OPFSVaultStorage` em
 * `store.ts:766-825` (HLOS-28), refatorada para:
 * 1. Aceitar root injetado (testável em jsdom com mockRoot).
 * 2. Expor `read`/`write`/`list`/`remove` como interface `VaultStorage`.
 * 3. Path traversal bloqueado (anti `..` injection).
 *
 * VaultStore passa `userId` no path; aqui só lidamos com a parte
 * relativa (`<userId>/<file>`) que vem do VaultStore (já validado).
 */
export class OPFSBackend implements VaultStorage {
  constructor(private readonly getRoot: () => Promise<FileSystemDirectoryHandle>) {}

  /**
   * Resolve um path relativo ("userId/file.json") para FileSystemFileHandle no OPFS.
   * Cria diretórios pai se necessário.
   * - Returns: null se path tentar escapar do root (path traversal).
   *
   * HLOS-168-B.2 (fix): retorna FileSystemFileHandle (que tem createWritable()),
   * NÃO File. O código anterior retornava File via getFile() e tentava chamar
   * createWritable() nele — File NÃO TEM esse método, quebrando write() em runtime.
   */
  private async resolveFileHandle(
    relativePath: string,
    options: { create?: boolean } = {},
  ): Promise<FileSystemFileHandle | null> {
    if (relativePath.includes("..") || relativePath.startsWith("/")) {
      return null;
    }
    const parts = relativePath.split("/").filter((p) => p.length > 0);
    if (parts.length === 0) return null;

    const root = await this.getRoot();
    let dir = root;
    for (let i = 0; i < parts.length - 1; i++) {
      dir = await dir.getDirectoryHandle(parts[i]!, { create: options.create ?? false });
    }
    return await dir.getFileHandle(parts[parts.length - 1]!, {
      create: options.create ?? false,
    });
  }

  async read(relativePath: string): Promise<Uint8Array | null> {
    try {
      const handle = await this.resolveFileHandle(relativePath);
      if (!handle) return null;
      const file = await handle.getFile();
      return new Uint8Array(await file.arrayBuffer());
    } catch (e) {
      if (e instanceof Error && e.name === "NotFoundError") return null;
      throw e;
    }
  }

  async write(relativePath: string, data: Uint8Array): Promise<void> {
    const handle = await this.resolveFileHandle(relativePath, { create: true });
    if (!handle) {
      throw new Error(`OPFSBackend.write: invalid path "${relativePath}"`);
    }
    const writable = await handle.createWritable();
    await writable.write(data as unknown as BufferSource);
    await writable.close();
  }

  async list(directory: string): Promise<string[]> {
    if (directory.includes("..") || directory.startsWith("/")) {
      return [];
    }
    const parts = directory.split("/").filter((p) => p.length > 0);
    const root = await this.getRoot();
    let dir = root;
    for (const part of parts) {
      try {
        dir = await dir.getDirectoryHandle(part, { create: false });
      } catch {
        return [];
      }
    }
    try {
      const entries: string[] = [];
      for await (const [name, handle] of (dir as any).entries()) {
        if (handle.kind === "file") {
          entries.push(name);
        }
      }
      return entries.sort();
    } catch {
      return [];
    }
  }

  async remove(relativePath: string): Promise<void> {
    const parts = relativePath.split("/").filter((p) => p.length > 0);
    if (parts.length === 0) return;
    if (parts.some((p) => p.includes(".."))) return;

    const root = await this.getRoot();
    let dir = root;
    for (let i = 0; i < parts.length - 1; i++) {
      try {
        dir = await dir.getDirectoryHandle(parts[i]!, { create: false });
      } catch {
        return; // parent dir doesn't exist
      }
    }
    try {
      await (dir as FileSystemDirectoryHandle).removeEntry(parts[parts.length - 1]!, {
        recursive: false,
      });
    } catch (e) {
      if (e instanceof Error && e.name === "NotFoundError") return;
      throw e;
    }
  }
}
